import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  Grid,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import axios from "../../../config/axiosConfig";
import PageHeader from "../../../components/layout/PageHeader";
import AsignarEquiposParOT from "../components/AsignarEquiposParOT";
import AsignarEquiposVariosOT from "../components/AsignarEquiposVariosOT";
import EmparejarEquiposOT from "../components/EmparejarEquiposOT";
import EmparejarChipsPOC from "../components/EmparejarChipsPOC";
import EntregaAccesoriosOT from "../components/EntregaAccesoriosOT";
import ConcluirOT from "../components/ConcluirOT";
import DevolucionEquiposOT from "../components/DevolucionEquiposOT";
import ClasificarDevolucionOT from "../components/ClasificarDevolucionOT";
import SeleccionarMantenimientoOT from "../components/SeleccionarMantenimientoOT";
import DiagnosticoMantenimientoOT from "../components/DiagnosticoMantenimientoOT";
import FinalizarMantenimientoOT from "../components/FinalizarMantenimientoOT";
import SeleccionarReemplazoOT from "../components/SeleccionarReemplazoOT";
import AsignarReemplazoOT from "../components/AsignarReemplazoOT";
import FinalizarReemplazoOT from "../components/FinalizarReemplazoOT";
import SeleccionarRemplazoVendidosActivosOT from "../components/SeleccionarRemplazoVendidosActivosOT";
import AsignarRemplazoVendidosActivosOT from "../components/AsignarRemplazoVendidosActivosOT";
import FinalizarRemplazoVendidosActivosOT from "../components/FinalizarRemplazoVendidosActivosOT";
import VenderEquipoYAccesoriosOT from "../components/VenderEquipoYAccesoriosOT";
import IngresoPlacaOT from "../components/IngresoPlacaOT";
import ActivarVendidoOT from "../components/ActivarVendidoOT";
import ConcluirVentaOT from "../components/ConcluirVentaOT";
import VenderEquipoActivoOT from "../components/VenderEquipoActivoOT";
import TraspasoActivosOT from "../components/TraspasoActivosOT";
import AsignarVendidoOT from "../components/AsignarVendidoOT";
import EmparejarModemChipOT from "../components/EmparejarModemChipOT";
import ConcluirActivarVendidosOT from "../components/ConcluirActivarVendidosOT";
import AsignarPruebaOT from "../components/AsignarPruebaOT";
import ConcluirPruebaOT from "../components/ConcluirPruebaOT";
import DesactivarVendidosOT from "../components/DesactivarVendidosOT";
import AdicionChipsOT from "../components/AdicionChipsOT";
import DevolucionChipsOT from "../components/DevolucionChipsOT";
import DevolverVentaOT from "../components/DevolverVentaOT";

// Servicios que quedan fuera del módulo de OT desde la búsqueda de cliente
// (AVL/Scada no usan ot_solicitud en este flujo). Venta es la excepción: SÍ maneja
// AVL/Scada con su propio paso inicial (ver puedeVender).
const SERVICIOS_SIN_ADICION = ["AVL", "Scada"];

// id de ot_tipo "Devolucion" (Devolución de equipos activos) — en Detalle no
// se agrega equipo nuevo, se lista lo que el cliente ya tiene para devolverlo.
const TIPO_OT_DEVOLUCION = 5;

// id de ot_tipo "Mantenimiento". Igual que Adición, AVL/Scada no entra por acá — ese
// Mantenimiento ya lo cubre el módulo Requerimiento (reqMantenimientoModel.js).
const TIPO_OT_MANTENIMIENTO = 2;

// id de ot_tipo "Mantenimiento Traspaso" — mismo flujo que Mantenimiento (se trata
// como esMantenimiento más abajo), solo que exclusivo de la sucursal Cochabamba
// (filtrado en FormularioOT.jsx y validado también en otSolicitudService.js).
const TIPO_OT_MANTENIMIENTO_TRASPASO = 8;

// id de ot_tipo "Mantenimiento Equipos Vendidos" — mismo flujo que Mantenimiento, pero además de los equipos Activos
// admite los Vendido Activo, que al terminar vuelven a su estado original (ver otMantenimientoModel.js).
const TIPO_OT_MANTENIMIENTO_VENDIDOS = 18;

// id de ot_tipo "Remplazo Equipos Vendidos Inactivos" — mismo flujo que Remplazo Vendidos Activos, pero con equipos 'Vendido'
// (comprados sin servicio activo): se listan y terminan en ese estado.
const TIPO_OT_REMPLAZO_VENDIDOS_INACTIVOS = 19;

// id de ot_tipo "Reemplazo". Igual criterio: AVL/Scada no entra por acá.
const TIPO_OT_REEMPLAZO = 4;

// id de ot_tipo "Remplazo Equipos Vendidos Activos" — mismo flujo de 3 pasos que
// Reemplazo (id=4), pero acá el equipo que sale ya está "Vendido Activo" (el cliente lo
// compró) y el equipo nuevo hereda esa condición (precio_venta + propiedad), salvo Chip
// que nunca se vende y queda simplemente "Activo". Igual criterio de alcance que
// Reemplazo: AVL/Scada no entra por acá.
const TIPO_OT_REMPLAZO_VENDIDOS_ACTIVOS = 16;

// id de ot_tipo "Venta de Equipo" — distinto de "Venta de Equipos Activos" (id=14, ver
// TIPO_OT_VENTA_ACTIVOS abajo: carpeta legacy separada, flujo más simple). A diferencia
// de Mantenimiento/Reemplazo, acá SÍ entra AVL/Scada (venta de módems, con su propio
// sub-flujo de placas).
const TIPO_OT_VENTA = 11;

// id de ot_tipo "Venta de Equipos Activos" — vende equipo que el cliente YA tiene
// Activo/en servicio (pasa a ser propiedad del cliente), no equipo Inactivo de stock
// como Venta de Equipo. Un solo paso (sin activación posterior, sin firma): salta
// directo a Finalizado para cualquier tipo de servicio.
const TIPO_OT_VENTA_ACTIVOS = 14;

// id de ot_tipo "Traspaso Equipos Activos" — mueve la sucursal de TRS que trackea un
// equipo que YA ESTÁ ACTIVO con el cliente (no equipo sin cliente — eso ya lo cubre
// Centro de Equipos > Traspaso, con su propio estado 'Transicion', totalmente
// independiente de las OT). Un solo paso, sin pantalla de "Finalizar" separada.
const TIPO_OT_TRASPASO_ACTIVOS = 10;

// id de ot_tipo "Activar Equipos Vendidos" — instala/activa en campo un equipo que ya
// está Vendido (de cualquier OT anterior, no necesariamente de la misma Venta),
// asignándole placa/grupo/ID cliente. Distinto del paso interno "Activar Equipo
// Vendido" que ya tiene Venta (ActivarVendidoOT/puedeActivarVenta): ese solo activa lo
// vendido en ESA MISMA OT; esta es una OT propia que cataloga cualquier equipo Vendido
// del cliente/servicio.
const TIPO_OT_ACTIVAR_VENDIDOS = 12;

// id de ot_tipo "Prueba" — equipo entregado a préstamo/prueba, no adición permanente.
// A diferencia de Adición normal, sí incluye AVL/Scada y marca el equipo con
// razon='prueba' (ver otPruebaModel.js). Concluir salta siempre a Finalizado, sin
// firma, sin importar el tipo de servicio.
const TIPO_OT_PRUEBA = 6;

// id de ot_tipo "Desactivar Equipos Vendidos" — inversa de Activar Equipos Vendidos
// (id=12): un equipo "Vendido Activo" (instalado/en servicio) vuelve a "Vendido" (sigue
// siendo del cliente, listo para una futura reactivación). Un solo paso, sin firma:
// salta directo a Finalizado, igual que Venta de Equipos Activos.
const TIPO_OT_DESACTIVAR_VENDIDOS = 13;

// id de ot_tipo "Devolucion Venta" — inversa de Venta de Equipo (id=11): un equipo
// "Vendido" (vendido pero nunca activado) vuelve a "Inactivo" (libre para una futura
// venta). Junto con Desactivar Equipos Vendidos (arriba) permite deshacer una venta
// completa hasta el final. Mismo criterio de un solo paso sin firma.
const TIPO_OT_DEVOLUCION_VENTA = 27;

// id de ot_tipo "Adicion de Chips" (solo AVL/Scada): un solo paso — se empareja cada chip Inactivo con un módem del cliente (el chip solo
// cambia al emparejarlo) y "Finalizar" cierra la OT, sin Asignado/Procesando/Entregado.
const TIPO_OT_ADICION_CHIPS = 28;

// id de ot_tipo "Devolucion de Chips" (solo AVL/Scada): un solo paso — se marcan los chips del cliente que no tienen módem y "Finalizar OT" los deja
// Inactivos y cierra la OT.
const TIPO_OT_DEVOLUCION_CHIPS = 29;

const ESTADOS = ["Eliminado", "Emitido", "Detalle", "Asignado", "Procesando", "Entregado", "Concluido", "Finalizado"];
// Un color por estado (mismo índice que ESTADOS) para que la barra se lea de un
// vistazo: rojo = eliminado, azules = arrancando, ámbar = en proceso, verdes = ya
// resuelto — en vez de un solo tono de texto para todo.
const ESTADO_COLOR = ["error", "info", "info", "primary", "warning", "secondary", "success", "success"];

const headerChipSx = { height: 19, fontSize: 10.5, fontWeight: 700 };

const OBSERVACION_LABEL = {
  5: "Observación de entrega",
  6: "Observación de conclusión",
};

const OrdenTrabajoPaso = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [orden, setOrden] = useState(null);
  const [loading, setLoading] = useState(true);

  const [avanzarOpen, setAvanzarOpen] = useState(false);
  const [avanzarObs, setAvanzarObs] = useState("");
  const [avanzando, setAvanzando] = useState(false);
  const [avanzarError, setAvanzarError] = useState("");

  const [finalizandoAdicion, setFinalizandoAdicion] = useState(false);
  // Cuántos equipos ya se guardaron en la OT (AsignarEquiposParOT/AsignarEquiposVariosOT)
  // — mientras sea 0, "Finalizar Adición de Equipos" queda bloqueado.
  const [adicionadosCount, setAdicionadosCount] = useState(0);

  // ── Devolución Fase 1 (selección de equipos, ver DevolucionEquiposOT.jsx) ──
  const [finalizandoDevolucion, setFinalizandoDevolucion] = useState(false);
  // Cuántos equipos/accesorios ya se guardaron como Devolucion en esta OT — mientras
  // sea 0, "Finalizar Devolución de Equipos" queda bloqueado para no confundirlo con
  // "Guardar Devolución" del formulario de abajo.
  const [devueltosCount, setDevueltosCount] = useState(0);
  // Dialog "Observaciones Generales" que pide el botón "Finalizar Devolución de
  // Equipos" antes de pasar la OT a "Asignado" y generar la Nota de Devolución.
  const [finalizarDevolucionOpen, setFinalizarDevolucionOpen] = useState(false);
  const [finalizarDevolucionObs, setFinalizarDevolucionObs] = useState("");
  const [finalizarDevolucionError, setFinalizarDevolucionError] = useState("");

  // ── Devolución Fase 2 (clasificación final, ver ClasificarDevolucionOT.jsx) ──
  // "Concluir Devolución" no tiene un paso de guardado separado: clasifica todo lo
  // pendiente (vía ref) y, si no hubo errores, concluye la OT en el mismo click.
  const [finalizandoClasificacion, setFinalizandoClasificacion] = useState(false);
  const clasificarRef = useRef();

  const [finalizandoEmparejamiento, setFinalizandoEmparejamiento] = useState(false);

  // ── Reemplazo (asignación de equipo nuevo, ver AsignarReemplazoOT.jsx) ──
  const [finalizandoAsignacionReemplazo, setFinalizandoAsignacionReemplazo] = useState(false);
  const [chipsEmparejados, setChipsEmparejados] = useState(0);
  const [solicitudFinalizarChips, setSolicitudFinalizarChips] = useState(0);
  const [chipsADevolver, setChipsADevolver] = useState(0);
  const [solicitudFinalizarDevChips, setSolicitudFinalizarDevChips] = useState(0);
  const [errorAsignacionReemplazo, setErrorAsignacionReemplazo] = useState("");

  // ── Remplazo Vendidos Activos (ver AsignarRemplazoVendidosActivosOT.jsx) ──
  const [finalizandoAsignacionRemplazoVendidosActivos, setFinalizandoAsignacionRemplazoVendidosActivos] = useState(false);
  const [errorAsignacionRemplazoVendidosActivos, setErrorAsignacionRemplazoVendidosActivos] = useState("");

  // ── Venta (ver VenderEquipoOT.jsx / IngresoPlacaOT.jsx / ActivarVendidoOT.jsx) ──
  // Dialog "Finalizar la Venta de Equipos" (espejo de concluyendo.php): pide Garantía
  // + Observación + si además de vender se activa el equipo con el cliente.
  const [finalizarVentaOpen, setFinalizarVentaOpen] = useState(false);
  const [finalizarVentaGarantia, setFinalizarVentaGarantia] = useState("");
  const [finalizarVentaObs, setFinalizarVentaObs] = useState("");
  const [finalizarVentaActivar, setFinalizarVentaActivar] = useState(false);
  const [finalizandoVenta, setFinalizandoVenta] = useState(false);
  const [finalizarVentaError, setFinalizarVentaError] = useState("");
  // Cuántas placas hay seleccionadas para esta OT — mientras IngresoPlacaOT no
  // reporte al menos 1, no se pasa a activar los módems vendidos.
  const [placasCount, setPlacasCount] = useState(0);
  // Cuántos equipos ya se vendieron en esta OT — bloquea "Finalizar la Venta de
  // Equipos" mientras siga en 0, para no cerrar una venta sin haber vendido nada.
  const [vendidosCount, setVendidosCount] = useState(0);
  const [finalizandoAsignacionVenta, setFinalizandoAsignacionVenta] = useState(false);
  const [errorAsignacionVenta, setErrorAsignacionVenta] = useState("");

  // ── Activar Equipos Vendidos (ver AsignarVendidoOT.jsx) ──
  const [finalizandoAsignacionActivarVendidos, setFinalizandoAsignacionActivarVendidos] = useState(false);
  const [errorAsignacionActivarVendidos, setErrorAsignacionActivarVendidos] = useState("");

  // ── Prueba (ver AsignarPruebaOT.jsx) ──
  const [finalizandoAsignacionPrueba, setFinalizandoAsignacionPrueba] = useState(false);
  const [errorAsignacionPrueba, setErrorAsignacionPrueba] = useState("");

  // ── Venta de Equipos Activos (ver VenderEquipoActivoOT.jsx) ──
  // Dialog "Finalizar Venta de Equipos Activos" (espejo de concluyendo.php, rama
  // Guardar): solo Garantía + Observación — sin toggle activar (acá no existe esa
  // bifurcación, siempre queda "Vendido Activo" directo) y sin campo Nota (boilerplate
  // fijo, no dato real de la OT).
  const [finalizarVentaActivosOpen, setFinalizarVentaActivosOpen] = useState(false);
  const [finalizarVentaActivosGarantia, setFinalizarVentaActivosGarantia] = useState("");
  const [finalizarVentaActivosObs, setFinalizarVentaActivosObs] = useState("");
  const [finalizandoVentaActivos, setFinalizandoVentaActivos] = useState(false);
  const [finalizarVentaActivosError, setFinalizarVentaActivosError] = useState("");
  const [vendidosActivosCount, setVendidosActivosCount] = useState(0);

  const [finalizandoOT, setFinalizandoOT] = useState(false);
  const [finalizarNombre, setFinalizarNombre] = useState("");
  const [finalizarCi, setFinalizarCi] = useState("");
  const [finalizarFecha, setFinalizarFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [finalizarHora, setFinalizarHora] = useState("");
  const [finalizarOTError, setFinalizarOTError] = useState("");
  const equiposRef = useRef();
  // Candado sincrónico (no depende de un re-render como el `disabled` del botón) contra
  // doble click/doble submit en los cambios de estado de la OT — se vio un caso real de
  // esto duplicando filas de historial (2 eventos "Cambio de Estado a Entregado" con 6
  // segundos de diferencia).
  const enviandoEstadoRef = useRef(false);

  useEffect(() => {
    axios.get(`/api/ot-solicitudes/${id}`)
      .then((res) => setOrden(res.data))
      .catch((err) => console.error("Error al cargar la orden", err))
      .finally(() => setLoading(false));
  }, [id]);

  const handleConfirmarAvanzar = () => {
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setAvanzando(true);
    setAvanzarError("");
    axios.patch(`/api/ot-solicitudes/${id}/avanzar`, { observacion: avanzarObs })
      .then((res) => {
        setOrden(res.data);
        setAvanzarOpen(false);
        setAvanzarObs("");
      })
      .catch((err) => {
        setAvanzarError(err?.response?.data?.error || "No se pudo avanzar el estado.");
      })
      .finally(() => {
        enviandoEstadoRef.current = false;
        setAvanzando(false);
      });
  };

  // Igual que guarda.php: guardar un equipo mientras la OT está "Emitido" la
  // pasa en silencio a "Detalle" (sin quedar registrado en historial_usu).
  const handleEquiposActualizados = () => {
    equiposRef.current?.refetch();
    if (Number(orden.estado) === 1) {
      axios.put(`/api/ot-solicitudes/${id}`, { estado: 2 })
        .then(() => setOrden((prev) => ({ ...prev, estado: 2 })))
        .catch((err) => console.error("No se pudo pasar la OT a Detalle", err));
    }
  };

  // Igual que el link "Finalizar la asignación de Equipos" de index.php: salta
  // directo a "Asignado", haya o no equipos guardados.
  const handleFinalizarAdicion = () => {
    if (!window.confirm('¿Finalizar la adición de equipos y pasar la OT a "Asignado"?')) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoAdicion(true);
    axios.patch(`/api/ot-solicitudes/${id}/finalizar-adicion`)
      .then((res) => setOrden(res.data))
      .catch((err) => alert(err?.response?.data?.error || "No se pudo finalizar la adición de equipos."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoAdicion(false);
      });
  };

  // Botón "Finalizar Devolución de Equipos" (Fase 1): antes de pasar a "Asignado"
  // pide la observación general (igual que devolucion_normal.php del sistema viejo).
  const handleFinalizarDevolucion = () => {
    setFinalizarDevolucionError("");
    setFinalizarDevolucionObs("");
    setFinalizarDevolucionOpen(true);
  };

  // Igual que ConcluirOT.jsx: abre la pestaña del PDF (en blanco) ya en el mismo gesto
  // de click, antes de cualquier await, para que el navegador no la bloquee como popup.
  const handleConfirmarFinalizarDevolucion = () => {
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoDevolucion(true);
    setFinalizarDevolucionError("");

    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.opener = null;
      pdfWindow.document.title = "Generando Nota de Devolución...";
      pdfWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Guardando la devolución y generando la Nota de Devolución...</div>';
    }

    axios.patch(`/api/ot-solicitudes/${id}/finalizar-seleccion-devolucion`, { observacion: finalizarDevolucionObs })
      .then(async (res) => {
        setOrden(res.data);
        setFinalizarDevolucionOpen(false);
        if (pdfWindow) {
          try {
            const { data } = await axios.get(`/api/ot-solicitudes/${id}/nota-devolucion.pdf`, { responseType: "blob" });
            const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
            pdfWindow.location.replace(pdfUrl);
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
          } catch {
            pdfWindow.close();
          }
        }
        // La Fase 2 (clasificar) normalmente la hace otro usuario/sesión más
        // adelante (ver ClasificarDevolucionOT.jsx) — en vez de quedarse en /paso
        // (que ya muestra Fase 2 directo, porque la OT pasó a "Asignado"), vuelve
        // al detalle de la OT. navigate(-1) en vez de construir la URL: /paso siempre
        // se llega empujando desde /detalle, así que "atrás" ya aterriza ahí sin dejar
        // una segunda entrada /detalle duplicada (ver el mismo criterio en "Volver al
        // detalle" más abajo).
        navigate(-1);
      })
      .catch((err) => {
        if (pdfWindow) pdfWindow.close();
        setFinalizarDevolucionError(err?.response?.data?.error || "No se pudo finalizar la devolución de equipos.");
      })
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoDevolucion(false);
      });
  };

  // Botón "Concluir Devolución" (Fase 2): un solo click clasifica todo lo que seguía
  // en "Espera" (vía ref a ClasificarDevolucionOT) y, si no hubo errores, pasa la OT
  // de "Procesando" a "Concluido".
  const handleFinalizarClasificacion = async () => {
    if (!window.confirm('¿Concluir la devolución? La OT pasará a "Concluido".')) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoClasificacion(true);
    try {
      const { fallidos } = (await clasificarRef.current?.guardarClasificacion()) || { fallidos: [] };
      if (fallidos.length) {
        alert(`No se pudo clasificar ${fallidos.length} equipo(s):\n${fallidos.join("\n")}`);
        return;
      }
      const res = await axios.patch(`/api/ot-solicitudes/${id}/finalizar-clasificacion-devolucion`);
      setOrden(res.data);
    } catch (err) {
      alert(err?.response?.data?.error || "No se pudo concluir la devolución.");
    } finally {
      enviandoEstadoRef.current = false;
      setFinalizandoClasificacion(false);
    }
  };

  // Igual que el link "Finalizar Emparejar Equipos" de ajustar.php: pasa a
  // "Procesando", haya o no baterías emparejadas.
  const handleFinalizarEmparejamiento = () => {
    if (!window.confirm('¿Finalizar el emparejamiento y pasar la OT a "Procesando"?')) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoEmparejamiento(true);
    axios.patch(`/api/ot-solicitudes/${id}/avanzar`, {})
      .then((res) => setOrden(res.data))
      .catch((err) => alert(err?.response?.data?.error || "No se pudo finalizar el emparejamiento."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoEmparejamiento(false);
      });
  };

  // Botón "Finalizar Asignación de Equipos" (Reemplazo): espejo de la validación de
  // estado.php — el backend rechaza si la cantidad de radios asignados no coincide
  // con la cantidad de radios reemplazados.
  const handleFinalizarAsignacionReemplazo = () => {
    if (!window.confirm('¿Finalizar la asignación de equipos y pasar la OT a "Procesando"?')) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoAsignacionReemplazo(true);
    setErrorAsignacionReemplazo("");
    axios.patch(`/api/ot-reemplazo/finalizar-asignacion/${id}`)
      .then((res) => setOrden(res.data))
      .catch((err) => setErrorAsignacionReemplazo(err?.response?.data?.error || "No se pudo finalizar la asignación de equipos."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoAsignacionReemplazo(false);
      });
  };

  // Botón "Finalizar Asignación de Equipos" (Remplazo Vendidos Activos): mismo criterio
  // que Reemplazo — el backend rechaza si queda algún retirado sin su reemplazo asignado.
  const handleFinalizarAsignacionRemplazoVendidosActivos = () => {
    if (!window.confirm('¿Finalizar la asignación de equipos y pasar la OT a "Procesando"?')) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoAsignacionRemplazoVendidosActivos(true);
    setErrorAsignacionRemplazoVendidosActivos("");
    axios.patch(`/api/ot-remplazo-vendidos-activos/finalizar-asignacion/${id}`)
      .then((res) => setOrden(res.data))
      .catch((err) => setErrorAsignacionRemplazoVendidosActivos(err?.response?.data?.error || "No se pudo finalizar la asignación de equipos."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoAsignacionRemplazoVendidosActivos(false);
      });
  };

  // Botón "Finalizar la Venta de Equipos" (espejo de concluyendo.php): abre el
  // diálogo que pide Garantía + Observación + si además se activa el equipo.
  const handleFinalizarVenta = () => {
    setFinalizarVentaError("");
    setFinalizarVentaGarantia("");
    setFinalizarVentaObs("");
    setFinalizarVentaActivar(false);
    setFinalizarVentaOpen(true);
  };

  const handleConfirmarFinalizarVenta = () => {
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoVenta(true);
    setFinalizarVentaError("");
    axios.patch(`/api/ot-venta/finalizar-venta/${id}`, {
      activar: finalizarVentaActivar,
      garantia: finalizarVentaGarantia,
      observacion: finalizarVentaObs,
    })
      .then((res) => {
        setOrden(res.data);
        setFinalizarVentaOpen(false);
      })
      .catch((err) => setFinalizarVentaError(err?.response?.data?.error || "No se pudo finalizar la venta."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoVenta(false);
      });
  };

  // Botón "Finalizar Venta de Equipos Activos" (espejo de concluyendo.php): a
  // diferencia de Venta normal, no hay bifurcación activar/no-activar — salta directo
  // a Finalizado para cualquier tipo de servicio.
  const handleFinalizarVentaActivos = () => {
    setFinalizarVentaActivosError("");
    setFinalizarVentaActivosGarantia("");
    setFinalizarVentaActivosObs("");
    setFinalizarVentaActivosOpen(true);
  };

  const handleConfirmarFinalizarVentaActivos = () => {
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoVentaActivos(true);
    setFinalizarVentaActivosError("");
    axios.patch(`/api/ot-venta-activos/finalizar/${id}`, {
      garantia: finalizarVentaActivosGarantia,
      observacion: finalizarVentaActivosObs,
    })
      .then((res) => {
        setOrden(res.data);
        setFinalizarVentaActivosOpen(false);
      })
      .catch((err) => setFinalizarVentaActivosError(err?.response?.data?.error || "No se pudo finalizar la venta de equipos activos."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoVentaActivos(false);
      });
  };

  // Botón "Finalizar Asignación de Equipos Vendidos" (Venta): espejo de estado.php,
  // sin validación de cantidades (a diferencia de Reemplazo).
  const handleFinalizarAsignacionVenta = () => {
    if (!window.confirm('¿Finalizar la asignación de equipos vendidos y pasar la OT a "Procesando"?')) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoAsignacionVenta(true);
    setErrorAsignacionVenta("");
    axios.patch(`/api/ot-venta/finalizar-asignacion/${id}`)
      .then((res) => setOrden(res.data))
      .catch((err) => setErrorAsignacionVenta(err?.response?.data?.error || "No se pudo finalizar la asignación de equipos vendidos."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoAsignacionVenta(false);
      });
  };

  // Botón "Finalizar Asignación de Equipos Vendidos" (Activar Equipos Vendidos): mismo
  // patrón que Venta, sin validación de cantidades.
  const handleFinalizarAsignacionActivarVendidos = () => {
    if (!window.confirm('¿Finalizar la asignación de equipos vendidos y pasar la OT a "Procesando"?')) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoAsignacionActivarVendidos(true);
    setErrorAsignacionActivarVendidos("");
    axios.patch(`/api/ot-activar-vendidos/finalizar-asignacion/${id}`)
      .then((res) => setOrden(res.data))
      .catch((err) => setErrorAsignacionActivarVendidos(err?.response?.data?.error || "No se pudo finalizar la asignación de equipos vendidos."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoAsignacionActivarVendidos(false);
      });
  };

  // Botón "Finalizar Asignación de Equipos" (Prueba): mismo patrón que Activar
  // Equipos Vendidos.
  const handleFinalizarAsignacionPrueba = () => {
    if (!window.confirm('¿Finalizar la asignación de equipos y pasar la OT a "Procesando"?')) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoAsignacionPrueba(true);
    setErrorAsignacionPrueba("");
    axios.patch(`/api/ot-prueba/finalizar-asignacion/${id}`)
      .then((res) => setOrden(res.data))
      .catch((err) => setErrorAsignacionPrueba(err?.response?.data?.error || "No se pudo finalizar la asignación de equipos."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoAsignacionPrueba(false);
      });
  };

  // Igual que index.php/Guarda_registro1.php del paso "Finalizar": pide quién
  // recibe (Nombre, C.I., Fecha, Hora) y lo guarda en `ruta`. Solo TKR muestra
  // este formulario (ver esTKR más abajo) y sus campos ya no son obligatorios
  // — el backend (finalizarOTTx) siempre toleró que vengan vacíos, así que acá
  // solo se saca la validación que los exigía.
  const handleFinalizarOT = () => {
    setFinalizarOTError("");
    if (!window.confirm("¿Finalizar esta OT? Quedará como Finalizado.")) return;
    if (enviandoEstadoRef.current) return;
    enviandoEstadoRef.current = true;
    setFinalizandoOT(true);
    axios.patch(`/api/ot-solicitudes/${id}/finalizar-ot`, {
      nombre: finalizarNombre.trim(),
      ci: finalizarCi.trim(),
      fecha: finalizarFecha,
      hora: finalizarHora,
    })
      .then((res) => setOrden(res.data))
      .catch((err) => setFinalizarOTError(err?.response?.data?.error || "No se pudo finalizar la OT."))
      .finally(() => {
        enviandoEstadoRef.current = false;
        setFinalizandoOT(false);
      });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }
  if (!orden) {
    return (
      <DashboardLayout>
        <Typography color="error">Orden no encontrada</Typography>
      </DashboardLayout>
    );
  }

  const estadoNum = Number(orden.estado);
  const esTKR = orden.tipo_servicio === "TKR";
  const esPOC = orden.tipo_servicio === "POC";
  const esDevolucion = Number(orden.tipo_ot) === TIPO_OT_DEVOLUCION;
  const esMantenimiento = [TIPO_OT_MANTENIMIENTO, TIPO_OT_MANTENIMIENTO_TRASPASO, TIPO_OT_MANTENIMIENTO_VENDIDOS].includes(Number(orden.tipo_ot));
  const esMantenimientoVendidos = Number(orden.tipo_ot) === TIPO_OT_MANTENIMIENTO_VENDIDOS;
  // Mantenimiento Traspaso: el paso de Cochabamba termina en "Enviar a Mantenimiento"
  // (imprime la Nota); el diagnóstico/reemplazo lo continúa un usuario de La Paz una
  // vez que el equipo llega físicamente — mismo criterio que escritorio_detalle.php
  // (bloqueaba solo cuando la sucursal del usuario que ve la OT era Cochabamba).
  const esMantenimientoTraspaso = Number(orden.tipo_ot) === TIPO_OT_MANTENIMIENTO_TRASPASO;
  const bloqueadoPorTraspasoCochabamba = esMantenimientoTraspaso && user?.sucursal === "Cochabamba";
  const esReemplazo = Number(orden.tipo_ot) === TIPO_OT_REEMPLAZO;
  const esRemplazoVendidosInactivos = Number(orden.tipo_ot) === TIPO_OT_REMPLAZO_VENDIDOS_INACTIVOS;
  // Los dos tipos comparten pasos y componentes; solo cambia el estado de los equipos ('Vendido Activo' o 'Vendido').
  const esRemplazoVendidosActivos = esRemplazoVendidosInactivos || Number(orden.tipo_ot) === TIPO_OT_REMPLAZO_VENDIDOS_ACTIVOS;
  const estadoVendido = esRemplazoVendidosInactivos ? "Vendido" : "Vendido Activo";
  const esVenta = Number(orden.tipo_ot) === TIPO_OT_VENTA;
  const esVentaActivos = Number(orden.tipo_ot) === TIPO_OT_VENTA_ACTIVOS;
  const esTraspasoActivos = Number(orden.tipo_ot) === TIPO_OT_TRASPASO_ACTIVOS;
  const esActivarVendidos = Number(orden.tipo_ot) === TIPO_OT_ACTIVAR_VENDIDOS;
  const esDesactivarVendidos = Number(orden.tipo_ot) === TIPO_OT_DESACTIVAR_VENDIDOS;
  const esDevolucionVenta = Number(orden.tipo_ot) === TIPO_OT_DEVOLUCION_VENTA;
  const esAdicionChips = Number(orden.tipo_ot) === TIPO_OT_ADICION_CHIPS;
  const esDevolucionChips = Number(orden.tipo_ot) === TIPO_OT_DEVOLUCION_CHIPS;
  const esPrueba = Number(orden.tipo_ot) === TIPO_OT_PRUEBA;
  const esAVL = orden.tipo_servicio === "AVL" || orden.tipo_servicio === "Scada";
  const puedeAdicionar =
    !esDevolucion && !esMantenimiento && !esReemplazo && !esRemplazoVendidosActivos && !esVenta && !esVentaActivos && !esTraspasoActivos && !esActivarVendidos && !esDesactivarVendidos && !esDevolucionVenta && !esAdicionChips && !esDevolucionChips && !esPrueba && !SERVICIOS_SIN_ADICION.includes(orden.tipo_servicio) && estadoNum >= 1 && estadoNum <= 2;
  // Desactivar Equipos Vendidos (espejo de index.php/guarda.php de
  // ot_desactivar_vendidos/): un solo paso, sin botón de header — el propio componente
  // ("Desactivar") resuelve la OT de una vez (guarda.php tampoco tiene un
  // concluyendo.php separado).
  const puedeDesactivarVendidos = esDesactivarVendidos && estadoNum >= 1 && estadoNum <= 2;
  // Devolucion Venta: mismo criterio de un solo paso que Desactivar Equipos Vendidos —
  // el propio componente ("Devolver") resuelve la OT de una vez.
  const puedeDevolverVenta = esDevolucionVenta && estadoNum >= 1 && estadoNum <= 2;
  // Adición de Chips: un solo paso en estado 1-2 (el propio componente empareja, deshace y finaliza).
  const puedeAdicionChips = esAdicionChips && estadoNum >= 1 && estadoNum <= 2;
  // Devolución de Chips: un solo paso en estado 1-2 (el propio componente elige y finaliza desde "Finalizar OT").
  const puedeDevolverChips = esDevolucionChips && estadoNum >= 1 && estadoNum <= 2;
  // Venta de Equipos Activos (espejo de index.php/guarda.php de ot_venta_activos/): un
  // solo paso, sin activación posterior — "Finalizar Venta de Equipos Activos" (header)
  // salta directo a Finalizado.
  const puedeVenderActivo = esVentaActivos && estadoNum >= 1 && estadoNum <= 2;
  // Traspaso de Equipos Activos (espejo de index.php/guarda.php de traspaso_activos/):
  // un solo paso, sin botón de header — el propio componente ("Guardar Traspaso")
  // resuelve la OT de una vez (guarda.php no tiene un concluyendo.php separado).
  const puedeTraspasarActivos = esTraspasoActivos && estadoNum >= 1 && estadoNum <= 2;
  // Activar Equipos Vendidos (espejo de index.php/guarda.php de ot_activar_vendidos/):
  // solo activar equipo acá. El botón "Finalizar Asignación de Equipos Vendidos"
  // (header) manda a Emparejar (3) si TKR/AVL, o directo a Procesando (4) si no.
  const puedeAsignarActivarVendidos = esActivarVendidos && estadoNum >= 1 && estadoNum <= 2;
  // Emparejar de Activar Equipos Vendidos (espejo de ajustar.php): paso separado,
  // igual que el PHP viejo — solo aplica a TKR (Radio↔Batería) y AVL/Scada
  // (Módem↔Chip). El botón de header reusa el genérico "Finalizar Emparejar Equipos"
  // (avanzarEstadoTx), igual que Adición.
  const puedeEmparejarActivarVendidos = esActivarVendidos && estadoNum === 3 && (esTKR || esAVL);
  // OT Prueba (espejo de index.php/guarda.php/ajustar.php/concluyendo.php de prueba/):
  // mismo esqueleto de estados que Activar Equipos Vendidos (Asignar en 1-2, Emparejar
  // en 3 si TKR/AVL, Concluir en 5 salta siempre a Finalizado sin firma).
  const puedeAsignarPrueba = esPrueba && estadoNum >= 1 && estadoNum <= 2;
  const puedeEmparejarPrueba = esPrueba && estadoNum === 3 && (esTKR || esAVL);
  const puedeConcluirPrueba = esPrueba && estadoNum === 5;
  const puedeDevolver = esDevolucion && estadoNum >= 1 && estadoNum <= 2;
  // Selección de equipos a Mantenimiento (espejo de index.php/guarda.php): un solo
  // paso, sin botón "Finalizar" aparte en el header — el propio componente ya avanza
  // la OT a "Asignado" al enviar.
  const puedeSeleccionarMantenimiento = esMantenimiento && estadoNum >= 1 && estadoNum <= 2;
  // Selección de equipo a reemplazar (espejo de index.php/asignacion.php de
  // remplazo/): mismo criterio de un solo paso que Mantenimiento.
  const puedeSeleccionarReemplazo = esReemplazo && estadoNum >= 1 && estadoNum <= 2;
  // Selección de equipo a reemplazar (Vendido Activo) — mismo criterio de un solo paso
  // que Reemplazo.
  const puedeSeleccionarRemplazoVendidosActivos = esRemplazoVendidosActivos && estadoNum >= 1 && estadoNum <= 2;
  // Vender equipo (espejo de index.php/guarda.php de ot_venta/): igual que Reemplazo,
  // un solo paso de selección; el botón "Finalizar la Venta de Equipos" (header, ver
  // handleFinalizarVenta) decide la bifurcación solo-venta vs vender-y-activar.
  const puedeVender = esVenta && estadoNum >= 1 && estadoNum <= 2;
  // Asignar equipo nuevo (Reemplazo) — se muestra junto a Emparejar (ya reusado tal
  // cual, ver puedeEmparejar) en el mismo estado "Asignado". El botón "Finalizar
  // Asignación de Equipos" (header) valida cantidades y avanza a "Procesando".
  const puedeAsignarReemplazo = esReemplazo && estadoNum === 3;
  // Asignar equipo nuevo (Remplazo Vendidos Activos) — mismo criterio que Reemplazo.
  const puedeAsignarRemplazoVendidosActivos = esRemplazoVendidosActivos && estadoNum === 3;
  // Venta (con activación): primero, si es AVL con cliente_servicio.ot_vehi=1, hay
  // que registrar/elegir al menos una placa (espejo de ingreso_placa.php) antes de
  // poder activar los módems vendidos.
  const puedeIngresarPlaca = esVenta && estadoNum === 3 && esAVL && Number(orden.ot_vehi) === 1 && placasCount === 0;
  const puedeActivarVenta = esVenta && estadoNum === 3 && !puedeIngresarPlaca;
  // Emparejar es de Adición (recién asignada); Devolución SÍ llega a "Asignado" ahora
  // (Fase 1 termina ahí, ver finalizarSeleccionDevolucionTx), pero ese estado significa
  // otra cosa para Devolución (Fase 2 pendiente, ver puedeClasificarDevolucion) — se
  // excluye acá para no mostrar Emparejar en ese caso. Venta usa su PROPIA instancia de
  // EmparejarEquiposOT (con estados "Asignado Vendido"/"Vendido Activo", ver
  // puedeActivarVenta) en vez de esta genérica, así que se excluye acá. Reemplazo
  // también se excluye a pedido del usuario: cada equipo retirado ya elige su
  // reemplazo puntual en AsignarReemplazoOT.jsx (panel "Buscar Reemplazo"), así que
  // emparejar radio↔batería nueva por separado ya no tiene sentido ahí — si se
  // reemplazan radio y batería juntas, quedan sin `relacion` explícita entre sí.
  const puedeEmparejar = !esDevolucion && !esMantenimiento && !esVenta && !esVentaActivos && !esTraspasoActivos && !esActivarVendidos && !esDesactivarVendidos && !esDevolucionVenta && !esAdicionChips && !esDevolucionChips && !esPrueba && !esReemplazo && !esRemplazoVendidosActivos && (esTKR || esPOC) && estadoNum === 3;
  // Diagnóstico de falla (+ reemplazo opcional) — ocupa en Mantenimiento el mismo
  // estado "Asignado" (3) que Emparejar ocupa en Adición.
  const puedeDiagnosticar = esMantenimiento && !bloqueadoPorTraspasoCochabamba && estadoNum === 3;
  // "Entrega de Accesorios" es de Adición (agregar accesorios nuevos al cliente);
  // Devolución no usa este estado para nada propio (su Fase 2 vive en "Asignado", ver
  // puedeClasificarDevolucion) — se excluye igual por si acaso. Mantenimiento y
  // Reemplazo SÍ la usan tal cual (reusan el mismo paso, no están excluidos).
  const puedeEntregar = !esDevolucion && !bloqueadoPorTraspasoCochabamba && estadoNum === 4;
  // "Asignado" (3) es el estado normal de entrada a la Fase 2 (ver
  // finalizarSeleccionDevolucionTx); 4-5 quedan cubiertos igual por si una OT de
  // Devolución vieja/mal-empujada quedó en "Procesando" o "Entregado" por la pantalla
  // legacy — así igual se puede clasificar y concluir desde acá en vez de quedar
  // atascada sin ningún paso disponible.
  const puedeClasificarDevolucion = esDevolucion && estadoNum >= 3 && estadoNum <= 5;
  // Mantenimiento, Reemplazo y Venta no usan el paso genérico "Concluir OT" (con
  // firma) — tienen su propio cierre, ver puedeFinalizarMantenimiento/
  // puedeFinalizarReemplazo/puedeConcluirVenta.
  const puedeConcluir = !esMantenimiento && !esReemplazo && !esRemplazoVendidosActivos && !esVenta && !esVentaActivos && !esTraspasoActivos && !esActivarVendidos && !esDesactivarVendidos && !esDevolucionVenta && !esAdicionChips && !esDevolucionChips && !esPrueba && estadoNum === 5;
  const puedeFinalizarMantenimiento = esMantenimiento && !bloqueadoPorTraspasoCochabamba && estadoNum === 5;
  const puedeFinalizarReemplazo = esReemplazo && estadoNum === 5;
  const puedeFinalizarRemplazoVendidosActivos = esRemplazoVendidosActivos && estadoNum === 5;
  const puedeConcluirActivarVendidos = esActivarVendidos && estadoNum === 5;
  // Concluir Venta (con precio final) — AVL/Scada salta directo a Finalizado (7)
  // desde acá; el resto queda en Concluido (6) y de ahí sigue el paso genérico de
  // firma de abajo (puedeFinalizar), sin cambios.
  const puedeConcluirVenta = esVenta && estadoNum === 5;
  const puedeFinalizar = estadoNum === 6;
  const sinPasoDisponible =
    !puedeAdicionar && !puedeDevolver && !puedeSeleccionarMantenimiento && !puedeSeleccionarReemplazo &&
    !puedeAsignarReemplazo && !puedeSeleccionarRemplazoVendidosActivos && !puedeAsignarRemplazoVendidosActivos &&
    !puedeFinalizarRemplazoVendidosActivos && !puedeEmparejar && !puedeDiagnosticar && !puedeEntregar &&
    !puedeClasificarDevolucion && !puedeConcluir && !puedeFinalizarMantenimiento &&
    !puedeFinalizarReemplazo && !puedeVender && !puedeIngresarPlaca && !puedeActivarVenta &&
    !puedeConcluirVenta && !puedeVenderActivo && !puedeTraspasarActivos &&
    !puedeAsignarActivarVendidos && !puedeEmparejarActivarVendidos && !puedeConcluirActivarVendidos &&
    !puedeDesactivarVendidos && !puedeDevolverVenta && !puedeAdicionChips && !puedeDevolverChips &&
    !puedeAsignarPrueba && !puedeEmparejarPrueba && !puedeConcluirPrueba && !puedeFinalizar;

  return (
    <DashboardLayout>
      <PageHeader
        title={`Orden de Trabajo #${orden.id}${orden.razon_social ? ` — ${orden.razon_social}` : ""}`}
        subtitle={
          <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            <Chip
              size="small"
              label={ESTADOS[estadoNum] || "—"}
              color={ESTADO_COLOR[estadoNum] || "default"}
              sx={headerChipSx}
            />
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={`Cod. Servicio: ${orden.id_servicio_cliente}`}
              sx={headerChipSx}
            />
            {orden.tipo_servicio && (
              <Chip size="small" variant="outlined" color="default" label={orden.tipo_servicio} sx={headerChipSx} />
            )}
            {orden.grupo && (
              <Chip size="small" variant="outlined" color="secondary" label={`Grupo: ${orden.grupo}`} sx={headerChipSx} />
            )}
            {orden.descripcion && (
              <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{orden.descripcion}</Typography>
            )}
          </Stack>
        }
        actions={
          <>
            {puedeAdicionar && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={finalizandoAdicion ? null : <ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarAdicion}
                disabled={finalizandoAdicion || adicionadosCount === 0}
                title={adicionadosCount === 0 ? "Primero adiciona al menos un equipo con el botón de abajo" : undefined}
              >
                {finalizandoAdicion ? <CircularProgress size={18} color="inherit" /> : "Finalizar Adición de Equipos"}
              </Button>
            )}
            {puedeDevolver && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarDevolucion}
                disabled={devueltosCount === 0}
                title={devueltosCount === 0 ? "Primero guarda al menos una devolución con el botón de abajo" : undefined}
              >
                Finalizar Devolución de Equipos
              </Button>
            )}
            {puedeClasificarDevolucion && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={finalizandoClasificacion ? null : <ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarClasificacion}
                disabled={finalizandoClasificacion}
              >
                {finalizandoClasificacion ? <CircularProgress size={18} color="inherit" /> : "Concluir Devolución"}
              </Button>
            )}
            {(puedeEmparejar || puedeEmparejarActivarVendidos || puedeEmparejarPrueba) && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={finalizandoEmparejamiento ? null : <ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarEmparejamiento}
                disabled={finalizandoEmparejamiento}
              >
                {finalizandoEmparejamiento ? <CircularProgress size={18} color="inherit" /> : "Finalizar Emparejar Equipos"}
              </Button>
            )}
            {puedeAsignarReemplazo && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={finalizandoAsignacionReemplazo ? null : <ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarAsignacionReemplazo}
                disabled={finalizandoAsignacionReemplazo}
              >
                {finalizandoAsignacionReemplazo ? <CircularProgress size={18} color="inherit" /> : "Finalizar Asignación de Equipos"}
              </Button>
            )}
            {puedeAsignarRemplazoVendidosActivos && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={finalizandoAsignacionRemplazoVendidosActivos ? null : <ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarAsignacionRemplazoVendidosActivos}
                disabled={finalizandoAsignacionRemplazoVendidosActivos}
              >
                {finalizandoAsignacionRemplazoVendidosActivos ? <CircularProgress size={18} color="inherit" /> : "Finalizar Asignación de Equipos"}
              </Button>
            )}
            {puedeAdicionChips && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={() => setSolicitudFinalizarChips((n) => n + 1)}
                disabled={!chipsEmparejados}
                title={!chipsEmparejados ? "Empareja al menos un chip antes de finalizar" : undefined}
              >
                Finalizar OT
              </Button>
            )}
            {puedeDevolverChips && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={() => setSolicitudFinalizarDevChips((n) => n + 1)}
                disabled={!chipsADevolver}
                title={!chipsADevolver ? "Marca al menos un chip antes de finalizar" : undefined}
              >
                Finalizar OT
              </Button>
            )}
            {puedeVender && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarVenta}
                disabled={!vendidosCount}
                title={!vendidosCount ? "Vende al menos un equipo antes de finalizar" : undefined}
              >
                Finalizar la Venta de Equipos
              </Button>
            )}
            {puedeActivarVenta && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={finalizandoAsignacionVenta ? null : <ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarAsignacionVenta}
                disabled={finalizandoAsignacionVenta}
              >
                {finalizandoAsignacionVenta ? <CircularProgress size={18} color="inherit" /> : "Finalizar Asignación de Equipos Vendidos"}
              </Button>
            )}
            {puedeAsignarActivarVendidos && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={finalizandoAsignacionActivarVendidos ? null : <ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarAsignacionActivarVendidos}
                disabled={finalizandoAsignacionActivarVendidos}
              >
                {finalizandoAsignacionActivarVendidos ? <CircularProgress size={18} color="inherit" /> : "Finalizar Asignación de Equipos Vendidos"}
              </Button>
            )}
            {puedeAsignarPrueba && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={finalizandoAsignacionPrueba ? null : <ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarAsignacionPrueba}
                disabled={finalizandoAsignacionPrueba}
              >
                {finalizandoAsignacionPrueba ? <CircularProgress size={18} color="inherit" /> : "Finalizar Asignación de Equipos"}
              </Button>
            )}
            {puedeVenderActivo && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={handleFinalizarVentaActivos}
                disabled={!vendidosActivosCount}
                title={!vendidosActivosCount ? "Vende al menos un equipo antes de finalizar" : undefined}
              >
                Finalizar Venta de Equipos Activos
              </Button>
            )}
            {puedeEntregar && (
              <Button
                variant="contained"
                color="success"
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={() => setAvanzarOpen(true)}
              >
                Finalizar Entrega
              </Button>
            )}
          </>
        }
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        {sinPasoDisponible && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              No hay ningún paso pendiente de trabajo para el estado actual de esta OT.
            </Typography>
            <Button
              variant="contained"
              onClick={() =>
                // navigate(-1) en vez de construir la URL: /paso siempre se llega
                // empujando desde /detalle (ver handleClicSiguiente ahí), así que "volver
                // atrás" ya aterriza justo en esa misma entrada — navegar hacia adelante
                // con replace (como antes) dejaba DOS entradas /detalle seguidas (la
                // original + esta), y el botón "←" de esa pantalla necesitaba dos clics
                // para realmente salir de la OT.
                navigate(-1)
              }
            >
              Volver al detalle
            </Button>
          </Paper>
        )}

        {puedeAdicionar && (
          <Box sx={{ mb: 1.5 }}>
            {esTKR ? (
              <AsignarEquiposParOT
                cod_ot={orden.id}
                cod_cliente={orden.cod_cliente}
                cod_servicio_cliente={orden.id_servicio_cliente}
                onEquiposActualizados={handleEquiposActualizados}
                onAsignadosCambiados={setAdicionadosCount}
              />
            ) : (
              <AsignarEquiposVariosOT
                cod_ot={orden.id}
                cod_cliente={orden.cod_cliente}
                cod_servicio_cliente={orden.id_servicio_cliente}
                servicio={orden.tipo_servicio}
                onEquiposActualizados={handleEquiposActualizados}
                onAsignadosCambiados={setAdicionadosCount}
              />
            )}
          </Box>
        )}

        {puedeDevolver && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Devolución de Equipos</Typography>
            <DevolucionEquiposOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              esTKR={esTKR}
              tipoServicio={orden.tipo_servicio}
              servicioDestino={orden.servicio_destino}
              onEquiposActualizados={handleEquiposActualizados}
              onDevueltosCambiados={setDevueltosCount}
            />
          </Paper>
        )}

        {puedeClasificarDevolucion && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Clasificar Devolución de Equipos</Typography>
            <ClasificarDevolucionOT
              ref={clasificarRef}
              cod_ot={orden.id}
              esTKR={esTKR}
            />
          </Paper>
        )}

        {puedeSeleccionarMantenimiento && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Enviar Equipos a Mantenimiento</Typography>
            <SeleccionarMantenimientoOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              esTKR={esTKR}
              esMantenimientoTraspaso={esMantenimientoTraspaso}
              esMantenimientoVendidos={esMantenimientoVendidos}
              onEnviado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeDiagnosticar && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Diagnóstico de Mantenimiento</Typography>
            <DiagnosticoMantenimientoOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              esTKR={esTKR}
              permitirReemplazo={!esMantenimientoVendidos}
              onFinalizado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeSeleccionarReemplazo && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Seleccionar Equipo a Reemplazar</Typography>
            <SeleccionarReemplazoOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              esTKR={esTKR}
              onEnviado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeAsignarReemplazo && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            {errorAsignacionReemplazo && (
              <Alert severity="error" sx={{ mb: 1.5 }}>{errorAsignacionReemplazo}</Alert>
            )}
            <Typography variant="h6" sx={{ mb: 1 }}>Asignar Equipo de Reemplazo</Typography>
            <AsignarReemplazoOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              esTKR={esTKR}
              onEquiposActualizados={() => {}}
            />
          </Paper>
        )}

        {puedeSeleccionarRemplazoVendidosActivos && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Seleccionar Equipo {estadoVendido} a Reemplazar</Typography>
            <SeleccionarRemplazoVendidosActivosOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              esTKR={esTKR}
              esPOC={esPOC}
              estadoVendido={estadoVendido}
              onEnviado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeAsignarRemplazoVendidosActivos && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            {errorAsignacionRemplazoVendidosActivos && (
              <Alert severity="error" sx={{ mb: 1.5 }}>{errorAsignacionRemplazoVendidosActivos}</Alert>
            )}
            <Typography variant="h6" sx={{ mb: 1 }}>Asignar Equipo de Reemplazo ({estadoVendido})</Typography>
            <AsignarRemplazoVendidosActivosOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              esTKR={esTKR}
              esPOC={esPOC}
              estadoVendido={estadoVendido}
              onEquiposActualizados={() => {}}
            />
          </Paper>
        )}

        {puedeEmparejar && (
          <Box sx={{ mb: 1.5 }}>
            {esTKR ? (
              <EmparejarEquiposOT cod_ot={orden.id} cod_servicio_cliente={orden.id_servicio_cliente} />
            ) : (
              <EmparejarChipsPOC cod_ot={orden.id} cod_servicio_cliente={orden.id_servicio_cliente} />
            )}
          </Box>
        )}

        {puedeVender && (
          <Box sx={{ mb: 1.5 }}>
            <VenderEquipoYAccesoriosOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              servicio={orden.tipo_servicio}
              esTKR={esTKR}
              esAVL={esAVL}
              onEnviado={() => {}}
              onVendidosCambiados={setVendidosCount}
            />
          </Box>
        )}

        {puedeVenderActivo && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Vender Equipo Activo</Typography>
            <VenderEquipoActivoOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              servicio={orden.tipo_servicio}
              esTKR={esTKR}
              esAVL={esAVL}
              onEnviado={() => {}}
              onVendidosCambiados={setVendidosActivosCount}
            />
          </Paper>
        )}

        {puedeTraspasarActivos && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Traspaso de Equipos Activos</Typography>
            <TraspasoActivosOT
              cod_ot={orden.id}
              cod_servicio_cliente={orden.id_servicio_cliente}
              servicio={orden.tipo_servicio}
              esTKR={esTKR}
              esAVL={esAVL}
              onEnviado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeIngresarPlaca && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Ingreso de Placa</Typography>
            <IngresoPlacaOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              onPlacasCambiadas={setPlacasCount}
            />
          </Paper>
        )}

        {puedeActivarVenta && (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
              {errorAsignacionVenta && (
                <Alert severity="error" sx={{ mb: 1.5 }}>{errorAsignacionVenta}</Alert>
              )}
              <Typography variant="h6" sx={{ mb: 1 }}>Activar Equipo Vendido</Typography>
              <ActivarVendidoOT
                cod_ot={orden.id}
                cod_cliente={orden.cod_cliente}
                cod_servicio_cliente={orden.id_servicio_cliente}
                esTKR={esTKR}
                esAVL={esAVL}
                otVehi={Number(orden.ot_vehi) === 1}
                onEquiposActualizados={() => {}}
              />
            </Paper>
            {/* EmparejarEquiposOT.jsx solo maneja Radio+Batería (igual que en
                Adición/Reemplazo) — el emparejamiento Modem↔Chip de ajustar.php
                para AVL/Scada no está cubierto todavía; se puede sumar aparte si
                hace falta. */}
            {esTKR && (
              <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Emparejar Equipos Vendidos</Typography>
                <EmparejarEquiposOT
                  cod_ot={orden.id}
                  cod_servicio_cliente={orden.id_servicio_cliente}
                  estadoNuevo="Asignado Vendido"
                  estadoActivo="Vendido Activo"
                />
              </Paper>
            )}
          </>
        )}

        {puedeAsignarActivarVendidos && (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
              {errorAsignacionActivarVendidos && (
                <Alert severity="error" sx={{ mb: 1.5 }}>{errorAsignacionActivarVendidos}</Alert>
              )}
              <Typography variant="h6" sx={{ mb: 1 }}>Activar Equipos Vendidos</Typography>
              <AsignarVendidoOT
                cod_ot={orden.id}
                cod_cliente={orden.cod_cliente}
                cod_servicio_cliente={orden.id_servicio_cliente}
                servicio={orden.tipo_servicio}
                esTKR={esTKR}
                esAVL={esAVL}
                onEnviado={() => {}}
                onActivadosCambiados={() => {}}
              />
            </Paper>
          </>
        )}

        {puedeEmparejarActivarVendidos && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Emparejar Equipos Vendidos</Typography>
            {esTKR ? (
              <EmparejarEquiposOT
                cod_ot={orden.id}
                cod_servicio_cliente={orden.id_servicio_cliente}
                estadoNuevo="Asignado Vendido"
                estadoActivo="Vendido Activo"
              />
            ) : (
              <EmparejarModemChipOT
                cod_ot={orden.id}
                cod_servicio_cliente={orden.id_servicio_cliente}
                estadoNuevo="Asignado Vendido"
                estadoActivo="Vendido Activo"
              />
            )}
          </Paper>
        )}

        {puedeDesactivarVendidos && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Desactivar Equipos Vendidos</Typography>
            <DesactivarVendidosOT
              cod_ot={orden.id}
              esTKR={esTKR}
              esAVL={esAVL}
              onFinalizado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeAdicionChips && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Adición de Chips</Typography>
            <AdicionChipsOT cod_ot={orden.id} onFinalizado={(nuevaOrden) => setOrden(nuevaOrden)} onEmparejados={setChipsEmparejados} solicitudFinalizar={solicitudFinalizarChips} />
          </Box>
        )}

        {puedeDevolverChips && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Devolución de Chips</Typography>
            <DevolucionChipsOT cod_ot={orden.id} onFinalizado={(nuevaOrden) => setOrden(nuevaOrden)} onSeleccionados={setChipsADevolver} solicitudFinalizar={solicitudFinalizarDevChips} />
          </Box>
        )}

        {puedeDevolverVenta && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Devolución de Venta</Typography>
            <DevolverVentaOT
              cod_ot={orden.id}
              esTKR={esTKR}
              esAVL={esAVL}
              onFinalizado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeAsignarPrueba && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            {errorAsignacionPrueba && (
              <Alert severity="error" sx={{ mb: 1.5 }}>{errorAsignacionPrueba}</Alert>
            )}
            <Typography variant="h6" sx={{ mb: 1 }}>Asignar Equipos a Prueba</Typography>
            <AsignarPruebaOT
              cod_ot={orden.id}
              cod_cliente={orden.cod_cliente}
              cod_servicio_cliente={orden.id_servicio_cliente}
              servicio={orden.tipo_servicio}
              esTKR={esTKR}
              esAVL={esAVL}
              onEnviado={() => {}}
              onAsignadosCambiados={() => {}}
            />
          </Paper>
        )}

        {puedeEmparejarPrueba && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Emparejar Equipos a Prueba</Typography>
            {esTKR ? (
              <EmparejarEquiposOT
                cod_ot={orden.id}
                cod_servicio_cliente={orden.id_servicio_cliente}
                razonActiva="prueba"
              />
            ) : (
              <EmparejarModemChipOT
                cod_ot={orden.id}
                cod_servicio_cliente={orden.id_servicio_cliente}
                razonActiva="prueba"
              />
            )}
          </Paper>
        )}

        {puedeEntregar && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Entrega de Accesorios</Typography>
            <EntregaAccesoriosOT cod_ot={orden.id} cod_cliente={orden.cod_cliente} servicio={orden.tipo_servicio} />
          </Paper>
        )}

        {puedeConcluir && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Concluir Orden de Trabajo</Typography>
            <ConcluirOT
              cod_ot={orden.id}
              esTKR={esTKR}
              onConcluido={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeFinalizarMantenimiento && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Finalizar Mantenimiento</Typography>
            <FinalizarMantenimientoOT
              cod_ot={orden.id}
              onFinalizado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeFinalizarReemplazo && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Finalizar Reemplazo</Typography>
            <FinalizarReemplazoOT
              cod_ot={orden.id}
              onFinalizado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeFinalizarRemplazoVendidosActivos && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Finalizar Remplazo de Equipos Vendidos Activos</Typography>
            <FinalizarRemplazoVendidosActivosOT
              cod_ot={orden.id}
              onFinalizado={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeConcluirVenta && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Concluir Venta</Typography>
            <ConcluirVentaOT
              cod_ot={orden.id}
              esTKR={esTKR}
              onConcluido={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeConcluirActivarVendidos && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Concluir Activación de Equipos Vendidos</Typography>
            <ConcluirActivarVendidosOT
              cod_ot={orden.id}
              cod_servicio_cliente={orden.id_servicio_cliente}
              esTKR={esTKR}
              esAVL={esAVL}
              onConcluido={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeConcluirPrueba && (
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Concluir Prueba</Typography>
            <ConcluirPruebaOT
              cod_ot={orden.id}
              onConcluido={(nuevaOrden) => setOrden(nuevaOrden)}
            />
          </Paper>
        )}

        {puedeFinalizar && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 1.5,
              borderTop: "3px solid",
              borderTopColor: "success.main",
              maxWidth: 680,
              mx: "auto",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: (t) => alpha(t.palette.success.main, t.palette.mode === "dark" ? 0.28 : 0.12),
                  color: "success.main",
                }}
              >
                <TaskAltIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  Finalizar Orden de Trabajo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {esTKR
                    ? "Último paso — datos de quien recibe la OT finalizada (opcional)."
                    : "Último paso."}
                </Typography>
              </Box>
            </Stack>

            {finalizarOTError && (
              <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setFinalizarOTError("")}>
                {finalizarOTError}
              </Alert>
            )}

            {/* Solo TKR pide estos datos — para el resto de los servicios se omite
                este formulario y "Finalizar OT" cierra la OT directamente. */}
            {esTKR && (
              <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Nombre"
                    fullWidth
                    size="small"
                    value={finalizarNombre}
                    onChange={(e) => setFinalizarNombre(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="C.I."
                    fullWidth
                    size="small"
                    value={finalizarCi}
                    onChange={(e) => setFinalizarCi(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeOutlinedIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Fecha"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={finalizarFecha}
                    onChange={(e) => setFinalizarFecha(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Hora"
                    type="time"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={finalizarHora}
                    onChange={(e) => setFinalizarHora(e.target.value)}
                  />
                </Grid>
              </Grid>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: esTKR ? 0 : 2 }}>
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={finalizandoOT ? null : <TaskAltIcon />}
                onClick={handleFinalizarOT}
                disabled={finalizandoOT}
                sx={{ px: 3 }}
              >
                {finalizandoOT ? <CircularProgress size={20} color="inherit" /> : "Finalizar OT"}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      <Dialog open={avanzarOpen} onClose={avanzando ? undefined : () => setAvanzarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Avanzar a "{ESTADOS[estadoNum + 1]}"
        </DialogTitle>
        <DialogContent>
          {avanzarError && <Alert severity="error" sx={{ mb: 2 }}>{avanzarError}</Alert>}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={OBSERVACION_LABEL[estadoNum + 1] || "Observación (opcional)"}
            value={avanzarObs}
            onChange={(e) => setAvanzarObs(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvanzarOpen(false)} disabled={avanzando}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarAvanzar} disabled={avanzando}>
            {avanzando ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={finalizarDevolucionOpen}
        onClose={finalizandoDevolucion ? undefined : () => setFinalizarDevolucionOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Finalizar Devolución de Equipos</DialogTitle>
        <DialogContent>
          {finalizarDevolucionError && <Alert severity="error" sx={{ mb: 2 }}>{finalizarDevolucionError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            La OT pasará a "Asignado" y se generará la Nota de Devolución. El equipo
            seleccionado queda en "Espera" hasta que se clasifique en el siguiente paso.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Observaciones Generales"
            value={finalizarDevolucionObs}
            onChange={(e) => setFinalizarDevolucionObs(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizarDevolucionOpen(false)} disabled={finalizandoDevolucion}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarFinalizarDevolucion} disabled={finalizandoDevolucion}>
            {finalizandoDevolucion ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={finalizarVentaOpen}
        onClose={finalizandoVenta ? undefined : () => setFinalizarVentaOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Finalizar la Venta de Equipos</DialogTitle>
        <DialogContent>
          {finalizarVentaError && <Alert severity="error" sx={{ mb: 2 }}>{finalizarVentaError}</Alert>}
          <TextField
            fullWidth
            label="Tiempo de Garantía (meses)"
            value={finalizarVentaGarantia}
            onChange={(e) => setFinalizarVentaGarantia(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Observaciones"
            value={finalizarVentaObs}
            onChange={(e) => setFinalizarVentaObs(e.target.value)}
            sx={{ mb: 2 }}
          />
          {!esAVL && (
            <FormControlLabel
              control={
                <Switch
                  checked={finalizarVentaActivar}
                  onChange={(e) => setFinalizarVentaActivar(e.target.checked)}
                />
              }
              label="Activar los equipos vendidos con el cliente"
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {esAVL
              ? 'La venta queda registrada sin activar equipo — la OT pasará directo a "Concluido". La activación se hace más adelante desde Requerimientos.'
              : finalizarVentaActivar
              ? 'La OT pasará a "Asignado" para asignar el equipo al servicio del cliente.'
              : 'La venta queda registrada sin activar equipo — la OT pasará directo a "Concluido".'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizarVentaOpen(false)} disabled={finalizandoVenta}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarFinalizarVenta} disabled={finalizandoVenta}>
            {finalizandoVenta ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={finalizarVentaActivosOpen}
        onClose={finalizandoVentaActivos ? undefined : () => setFinalizarVentaActivosOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Finalizar Venta de Equipos Activos</DialogTitle>
        <DialogContent>
          {finalizarVentaActivosError && <Alert severity="error" sx={{ mb: 2 }}>{finalizarVentaActivosError}</Alert>}
          <TextField
            fullWidth
            label="Tiempo de Garantía (meses)"
            value={finalizarVentaActivosGarantia}
            onChange={(e) => setFinalizarVentaActivosGarantia(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Observaciones"
            value={finalizarVentaActivosObs}
            onChange={(e) => setFinalizarVentaActivosObs(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizarVentaActivosOpen(false)} disabled={finalizandoVentaActivos}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarFinalizarVentaActivos} disabled={finalizandoVentaActivos}>
            {finalizandoVentaActivos ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default OrdenTrabajoPaso;
